import { useState } from "react";
import { usePage } from "@inertiajs/react";
import { BoltIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { IntegrationDrawer } from "@components/integrations/IntegrationDrawer";
import {
  ZapierPanel,
  type ApiToken,
  type ZapierWebhook,
  type RevealedToken,
} from "@components/integrations/ZapierPanel";
import {
  GenericWebhooksPanel,
  type GenericWebhook,
  type RevealedWebhookSecret,
  type TestResult,
} from "@components/integrations/GenericWebhooksPanel";

type IntegrationsProps = {
  api_tokens: ApiToken[];
  zapier_webhooks: ZapierWebhook[];
  generic_webhooks: GenericWebhook[];
  zapier_invite_url: string | null;
  revealed_token: RevealedToken | null;
  revealed_webhook_secret: RevealedWebhookSecret | null;
  test_result: TestResult | null;
};

type DrawerKey = "zapier" | "generic" | null;

export default function Integrations() {
  const {
    api_tokens,
    zapier_webhooks,
    generic_webhooks,
    zapier_invite_url,
    revealed_token,
    revealed_webhook_secret,
    test_result,
  } = usePage<IntegrationsProps>().props;

  // Auto-open a drawer when a reveal/test flash arrives.
  const initialDrawer: DrawerKey = revealed_token
    ? "zapier"
    : revealed_webhook_secret || test_result
    ? "generic"
    : null;

  const [openDrawer, setOpenDrawer] = useState<DrawerKey>(initialDrawer);

  return (
    <main className="min-h-screen bg-[#FAFAF9]">
      <div className="px-4 py-6 lg:px-12 lg:py-10">
        <div className="max-w-[1100px]">
          <h1 className="font-['IBM_Plex_Serif'] text-[28px] font-semibold text-[#2E3238]">
            Integrations
          </h1>
          <p className="mt-2 font-['Plus_Jakarta_Sans'] text-sm text-[#74767A]">
            Send your Launch10 leads anywhere — Zapier, your CRM, or any HTTPS endpoint.
          </p>

          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <IntegrationCard
              name="Zapier"
              tagline="Connect to 6,000+ apps"
              description="Send new leads to Google Sheets, Slack, HubSpot, Mailchimp, and thousands more."
              status={
                zapier_webhooks.length > 0
                  ? { label: `${zapier_webhooks.length} active`, tone: "active" }
                  : api_tokens.length > 0
                  ? { label: "Ready to connect", tone: "ready" }
                  : { label: "Not connected", tone: "idle" }
              }
              logo={<ZapierLogo />}
              onClick={() => setOpenDrawer("zapier")}
            />
            <IntegrationCard
              name="Generic Webhook"
              tagline="Any HTTPS endpoint"
              description="POST signed JSON to your own backend. Verify with HMAC-SHA256."
              status={
                generic_webhooks.length > 0
                  ? { label: `${generic_webhooks.length} active`, tone: "active" }
                  : { label: "Not configured", tone: "idle" }
              }
              logo={
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#EEF0FB]">
                  <BoltIcon className="h-6 w-6 text-[#5867C4]" />
                </div>
              }
              onClick={() => setOpenDrawer("generic")}
            />
            <ComingSoonCard />
          </div>
        </div>
      </div>

      <IntegrationDrawer
        open={openDrawer === "zapier"}
        onClose={() => setOpenDrawer(null)}
        title="Zapier"
        subtitle="Connect Launch10 leads to 6,000+ apps"
        icon={<ZapierLogo />}
      >
        <ZapierPanel
          apiTokens={api_tokens}
          zapierWebhooks={zapier_webhooks}
          zapierInviteUrl={zapier_invite_url}
          revealedToken={revealed_token}
        />
      </IntegrationDrawer>

      <IntegrationDrawer
        open={openDrawer === "generic"}
        onClose={() => setOpenDrawer(null)}
        title="Generic Webhook"
        subtitle="Send signed JSON to any HTTPS endpoint"
        icon={
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#EEF0FB]">
            <BoltIcon className="h-5 w-5 text-[#5867C4]" />
          </div>
        }
      >
        <GenericWebhooksPanel
          webhooks={generic_webhooks}
          revealedSecret={revealed_webhook_secret}
          testResult={test_result}
        />
      </IntegrationDrawer>
    </main>
  );
}

interface IntegrationCardProps {
  name: string;
  tagline: string;
  description: string;
  status: { label: string; tone: "active" | "ready" | "idle" };
  logo: React.ReactNode;
  onClick: () => void;
}

function IntegrationCard({ name, tagline, description, status, logo, onClick }: IntegrationCardProps) {
  const toneClasses = {
    active: "bg-[#F0FAF5] text-[#2E9E72] border-[#BDE8D2]",
    ready: "bg-[#FEF7E7] text-[#92400E] border-[#F5D5B5]",
    idle: "bg-[#F5F5F4] text-[#74767A] border-[#E5E5E4]",
  }[status.tone];

  const dotColor = {
    active: "bg-[#2E9E72]",
    ready: "bg-[#D97706]",
    idle: "bg-[#96989B]",
  }[status.tone];

  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative flex h-full flex-col rounded-2xl border border-[#E5E5E4] bg-white p-6 text-left transition-all hover:border-[#5867C4] hover:shadow-md"
    >
      <div className="flex items-start justify-between">
        {logo}
        <span
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-['Plus_Jakarta_Sans'] text-xs font-medium ${toneClasses}`}
        >
          <span className={`inline-flex h-1.5 w-1.5 rounded-full ${dotColor}`} />
          {status.label}
        </span>
      </div>
      <h3 className="mt-4 font-['Plus_Jakarta_Sans'] text-lg font-semibold text-[#2E3238]">
        {name}
      </h3>
      <p className="mt-0.5 font-['Plus_Jakarta_Sans'] text-sm font-medium text-[#5867C4]">
        {tagline}
      </p>
      <p className="mt-2 font-['Plus_Jakarta_Sans'] text-sm text-[#74767A]">{description}</p>
      <span className="mt-auto pt-4 font-['Plus_Jakarta_Sans'] text-sm font-medium text-[#2E3238] group-hover:text-[#5867C4]">
        Configure →
      </span>
    </button>
  );
}

function ComingSoonCard() {
  return (
    <div className="flex h-full flex-col items-start rounded-2xl border border-dashed border-[#D3D2D0] bg-transparent p-6">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#F5F5F4]">
        <SparklesIcon className="h-5 w-5 text-[#96989B]" />
      </div>
      <h3 className="mt-4 font-['Plus_Jakarta_Sans'] text-lg font-semibold text-[#96989B]">
        More coming soon
      </h3>
      <p className="mt-2 font-['Plus_Jakarta_Sans'] text-sm text-[#96989B]">
        HubSpot, Salesforce, and Google Sheets native integrations are on the roadmap.
      </p>
    </div>
  );
}

function ZapierLogo() {
  return (
    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#FEF1EE]">
      <svg viewBox="0 0 32 32" className="h-6 w-6" fill="#FF4A00" aria-hidden>
        <path d="M20 16a4 4 0 0 1-.24 1.37l-3.74 1.63-.02.02 1.63 3.74a4 4 0 0 1-2.74 0l1.63-3.74-.02-.02-3.74-1.63a4 4 0 0 1 0-2.74l3.74-1.63.02-.02-1.63-3.74a4 4 0 0 1 2.74 0l-1.63 3.74.02.02 3.74 1.63A4 4 0 0 1 20 16Z" />
        <path d="M30 14h-6.13l4.33-4.33-2.83-2.83L21.04 11.2l1.13-6.13h-4l1.13 6.13L15.04 6.84 12.2 9.67 16.54 14H10v4h6.54l-4.34 4.33 2.83 2.83 4.33-4.33-1.13 6.13h4l-1.13-6.13 4.33 4.33 2.83-2.83L23.87 18H30v-4Z" opacity="0.15" />
      </svg>
    </div>
  );
}
